import {ChangeDetectionStrategy, Component, Input, ViewChild} from '@angular/core';
import {
  DeclarationType,
  HarbourDuesDeclarationSummary,
  OrderStatus,
  TaskMessageStatus,
  TaskStatus,
  VisitSummaryStatus
} from "@portbase/bezoekschip-service-typescriptmodels";
import {NewVisitSummary} from "../visit-overview-item.component";
import {DeclarationMessageStatus, VisitOverviewUtils} from "../visit-overview.utils";
import {TooltipComponent} from "../../../../common/tooltip/tooltip.component";
import {ClearanceService} from "../../../../visit-details/visit/clearance/clearance-service";
import {AppContext} from "../../../../app-context";
import {lodash, nonNull} from "../../../../common/utils";
import moment from "moment";

@Component({
    selector: 'app-visit-overview-service-status',
    templateUrl: './visit-overview-service-status.component.html',
    styleUrls: ['./visit-overview-service-status.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class VisitOverviewServiceStatusComponent {
  _summary: NewVisitSummary;
  _declarationTypes: DeclarationType[];
  messageStatus: TaskMessageStatus;
  messageStatuses: DeclarationMessageStatus[] = [];
  _orderStatus: boolean;
  _inspection: boolean;
  _statusIndicatorButtonId: string;
  _statusIndicatorSpanId: string;

  protected harbourDues: boolean;
  protected sortedHarbourDues: HarbourDuesDeclarationSummary[];

  @ViewChild("tooltipComponent") tooltipComponent: TooltipComponent;

  constructor(protected clearanceService: ClearanceService) {
  }

  @Input() set orderStatus(orderStatus: boolean) {
    this._orderStatus = orderStatus;
    this.updateMessageStatus();
  }

  @Input() set inspectionStatus(inspection: boolean) {
    this._inspection = inspection;
    this.updateMessageStatus();
  }

  @Input()
  set summary(summary: NewVisitSummary) {
    this._summary = summary;
    this.updateMessageStatus();
  }

  @Input()
  set declarationTypes(declarationTypes: DeclarationType[]) {
    this._declarationTypes = declarationTypes;
    this.updateMessageStatus();
  }

  @Input("harbourDues")
  set setHarbourDues(value: boolean) {
    this.harbourDues = value;
    this.updateMessageStatus();
  }

  @Input()
  set statusIndicatorButtonId(statusIndicatorButtonId: string) {
    this._statusIndicatorButtonId = statusIndicatorButtonId;
  }

  @Input()
  set statusIndicatorSpanId(statusIndicatorSpanId: string) {
    this._statusIndicatorSpanId = statusIndicatorSpanId;
  }

  protected updateMessageStatus() {
    if (this._orderStatus) {
      this.setBasedOnOrderStatus();
      let bunkeringStatus = this._summary.taskStatuses[DeclarationType.BUNKERING];
      if (bunkeringStatus && bunkeringStatus.status != TaskMessageStatus.UNKNOWN) {
        let bunkeringTaskStatus = VisitOverviewUtils.getBunkeringTaskStatuses(this._summary);
        this.messageStatuses = this.messageStatuses.concat(bunkeringTaskStatus);
        this.messageStatus = VisitOverviewUtils.reduceLeastTaskMessageStatus([bunkeringTaskStatus[0].taskStatus, this.messageStatus])
      }
    } else {
      this.messageStatuses = VisitOverviewUtils.getTaskStatuses(this._summary, this._declarationTypes, this.clearanceService);
      this.messageStatus = VisitOverviewUtils.getLeastTaskStatusByDeclarations(this._summary, this._declarationTypes, this.clearanceService);
    }
    if (this._inspection) {
      this.messageStatuses.push(this.getInspectionStatus());
      this.messageStatus = VisitOverviewUtils.getLeastTaskMessageStatus(this.messageStatuses);
    }
    if (this.harbourDues) {
      this.sortedHarbourDues = lodash.orderBy(this._summary.harbourDues, h => h.invoiceStart, ["desc"]);
      this.messageStatuses.push(this.getHarbourDuesStatuses());
      this.messageStatus = VisitOverviewUtils.getLeastTaskMessageStatus(this.messageStatuses);
    }
  }

  protected setBasedOnOrderStatus = () => {
    this.messageStatus = this.orderStatusToTaskStatus(this._summary.orderStatus) ||
      (this._summary.visitStatus === VisitSummaryStatus.DEPARTED ? TaskMessageStatus.ACCEPTED : TaskMessageStatus.UNKNOWN);
    let deadlineLabel;
    const message = this.orderStatusMessage(this._summary.orderStatus);
    if (!this._summary.orderStatus && this._summary.deadlines) {
      const status = VisitOverviewUtils.getDeadlineInfo(this._summary, "ORDER");
      if (status) {
        this.messageStatus = status.messageStatus;
        deadlineLabel = status.deadlineLabel;
      }
    }

    const shorePowerStatus = this._summary.currentBerthVisit?.shorePowerStatus;
    this.messageStatuses = [{
      name: "Pilot, tugboat and boatmen",
      taskStatus: this.getMessageStatus(message),
      messages: message ? [message] : [],
      customLabel: deadlineLabel
    }, {
      name: "Shore Power",
      taskStatus: this.shorePowerStatusAsTaskMessageStatus(shorePowerStatus),
      messages: [],
      customLabel: this.renderShorePowerStatus(shorePowerStatus)
    }];
  }


  private shorePowerStatusAsTaskMessageStatus = (taskStatus: TaskStatus): TaskMessageStatus => {
    if (!taskStatus || !taskStatus.status) {
      return TaskMessageStatus.DISABLED;
    }
    return taskStatus.status;
  }

  private renderShorePowerStatus = (taskStatus: TaskStatus): string => {
    if (!taskStatus || !taskStatus.status) {
      return;
    }
    switch (taskStatus.status) {
      case TaskMessageStatus.SCHEDULED:
        return "Scheduled until ATA";
      case TaskMessageStatus.PENDING:
        return "Ordered";
      case TaskMessageStatus.REJECTED:
        return "Technical error";
      case TaskMessageStatus.ACCEPTED:
        return "Accepted";
    }
  }

  private getMessageStatus = (message: string): TaskMessageStatus => {
    if (TaskMessageStatus.ACCEPTED == this.messageStatus && message != null) {
        return TaskMessageStatus.WARNING;
    }
    return this.messageStatus;
  }

  private orderStatusToTaskStatus = (orderStatus: OrderStatus): TaskMessageStatus => {
    switch (orderStatus) {
      case OrderStatus.CANCELLED:
        return TaskMessageStatus.REJECTED;
      case OrderStatus.CONFIRMED:
        return TaskMessageStatus.ACCEPTED;
      case OrderStatus.CONFIRMED_NEW_PROPOSAL:
        return TaskMessageStatus.WARNING;
      case OrderStatus.ETA_CHANGED:
        return TaskMessageStatus.ACCEPTED_WITH_CONDITIONS;
      case OrderStatus.ETD_CHANGED:
        return TaskMessageStatus.ACCEPTED_WITH_CONDITIONS;
      case OrderStatus.ORDERED:
        return TaskMessageStatus.PENDING;
      case OrderStatus.REEVALUATED:
        return TaskMessageStatus.PENDING;
      case OrderStatus.REJECTED:
        return TaskMessageStatus.REJECTED;
    }
  }

  private orderStatusMessage = (orderStatus: OrderStatus): string => {
    switch (orderStatus) {
      case OrderStatus.REJECTED:
      case OrderStatus.CANCELLED:
        return this._summary.orderRejectReason;
      case OrderStatus.CONFIRMED:
      case OrderStatus.CONFIRMED_NEW_PROPOSAL:
        return VisitOverviewUtils.getMessageRescheduled(this._summary.berthVisitSearches, this._summary.incomingMovementHarbourMasterInfo);
      case OrderStatus.ETA_CHANGED:
        return "ETA has changed";
      case OrderStatus.ETD_CHANGED:
        return "ETD has changed";
      default:
        return null;
    }
  }

  private getInspectionStatus = (): DeclarationMessageStatus => {
    let inspectionsStarted = false;
    let inspectionsCompleted = false;
    if (AppContext.isAdmin() || AppContext.isInspectionHandler()) {
      const statuses = lodash.values(this._summary.cargoAgentStatuses);
      inspectionsStarted = statuses.some(s => s.inspectionsStarted);
      inspectionsCompleted = inspectionsStarted && statuses.filter(s => s.inspectionsStarted).every(s => s.inspectionsCompleted);
    } else if (AppContext.isInspectionViewer()) {
      const status = this._summary.cargoAgentStatuses[AppContext.userProfile.organisation?.shortName];
      inspectionsStarted = status && status.inspectionsStarted;
      inspectionsCompleted = status && status.inspectionsCompleted;
    }
    if (inspectionsCompleted) {
      return {
        taskStatus: TaskMessageStatus.ACCEPTED,
        name: "Inspection",
        messages: []
      }
    } else if (inspectionsStarted) {
      return {
        taskStatus: TaskMessageStatus.WARNING,
        name: "Inspection",
        customLabel: "Inspection reported",
        messages: []
      }
    }
    return {
      taskStatus: TaskMessageStatus.DISABLED,
      name: "Inspection",
      messages: []
    }
  }

  private getHarbourDuesStatuses(): DeclarationMessageStatus {
    let taskStatus = TaskMessageStatus.UNKNOWN;
    let messages: string[] = [];

    if (this._summary.harbourDuesDeclarations) {
      const leastStatus = VisitOverviewUtils.reduceLeastTaskMessageStatus(
        Object.values(this._summary.harbourDuesDeclarations).map(h => h.status));

      taskStatus = leastStatus !== TaskMessageStatus.DISABLED ? leastStatus : TaskMessageStatus.UNKNOWN;
      messages = leastStatus === TaskMessageStatus.REJECTED
        ? Object.values(this._summary.harbourDuesDeclarations).map(s => s.rejectionReason).filter(nonNull)
        : [];
    }

    if (this._summary.portUnCode === 'NLRTM') {
      const deadline = this._summary.deadlines?.["HARBOUR_DUES"];
      if (deadline && taskStatus === TaskMessageStatus.UNKNOWN) {
        const now = moment();
        if (deadline.end && now.isAfter(moment(deadline.end))) {
          taskStatus = TaskMessageStatus.DEADLINE_MISSED;
        } else if (deadline.start && now.isAfter(moment(deadline.start))) {
          taskStatus = TaskMessageStatus.DEADLINE_CLOSE;
        }
      }
    }

    return <DeclarationMessageStatus>{
      taskStatus,
      name: "Harbour dues",
      messages,
    };
  }

  protected trackByHarbourDuesId =
    (harbourDues: HarbourDuesDeclarationSummary) => harbourDues.crn + harbourDues.statementNumber;
}
